// --- Core ---
import {cloneElement, ReactElement, ReactNode, useRef, useState} from "react";
// --- Material UI ---
import {Avatar, Box, Popover, PopoverProps, Stack, Typography} from "@mui/material";

interface ComplexTooltipProps {
  children: ReactElement;
  /**
   * Hide the tooltip in any case
   */
  hidden?: boolean;
  content: ReactElement;
  popoverProps?: Partial<PopoverProps>;
}

const ComplexTooltip = ({children, content, hidden, popoverProps}: ComplexTooltipProps) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);

  if (!children) {
    return null;
  }

  if (!content || hidden) {
    return children;
  }

  const handlePopoverOpen = () => {
    setOpen(true);
  };

  const handlePopoverClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Box
        component="span" sx={{cursor: 'default', mr: 0.5}}
        ref={anchorEl}
        onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}
      >
        {cloneElement(children, children.props)}
      </Box>
      <Popover
        anchorEl={anchorEl.current}
        anchorOrigin={
          {
            vertical: 'center',
            horizontal: 'right'
          }
        }
        disableRestoreFocus
        onClose={handlePopoverClose}
        open={open}
        slotProps={
          {
            paper: {
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {m: 2, pointerEvents: 'auto'}
            }
          }
        }
        sx={
          {
            pointerEvents: 'none'
          }
        }
        transformOrigin={
          {
            vertical: 'center',
            horizontal: 'left'
          }
        }
        {...popoverProps}
      >
        {content}
      </Popover>
    </>
  );
};

interface ExtendedTooltipProps extends Omit<ComplexTooltipProps, "children" | "content"> {
  // Icon to display as an avatar for the tooltip
  avatar: ReactElement;
  // Elements displayed below the title, wrapped in a Stack
  children: ReactNode;
  // Text to display normally, that shows the tooltip when hovered
  label: ReactNode;
  // Title to show at the top of the tooltip. Uses label, when not specified
  title?: ReactNode;
}

const ExtendedTooltip = ({avatar, children, label, title, ...rest}: ExtendedTooltipProps) => (
  <ComplexTooltip
    content={
      <Stack sx={{m: 1}} direction="row" spacing={1}>
        <Avatar>
          {avatar}
        </Avatar>
        <Stack spacing={1}>
          <Typography variant="subtitle1">
            {title || label}
          </Typography>
          {children}
        </Stack>
      </Stack>
    }
    {...rest}
  >
    <>{label}</>
  </ComplexTooltip>
);

export {
  ComplexTooltip,
  ExtendedTooltip
};
